/**
 * Where a trainer goes after starting a class — ONE definition.
 *
 * `POST /batches/:id/start-meeting` answers with `{ zoomMode, meetingProvider }`
 * and the caller has to decide between the embedded classroom and opening the
 * provider's own link in a new tab. That two-line decision was copy-pasted into
 * four screens (batch detail ×2, the instructor profile, the trainer dashboard)
 * and every new provider means finding all four again — which is how Zoho
 * institutes ended up falling through to `window.open(batch.meetingLink)` on
 * three of them.
 *
 * The rule: a provider that runs INSIDE the LMS goes to `/i/classroom/:id`.
 * That is Zoom v2, platform-hosted Zoom, and Grats Meet — which never has a
 * link to open, so falling through for it would leave the trainer on a page
 * with a live class and no way into it.
 *
 * Zoho Meeting goes there too, although the meeting itself opens in Zoho's own
 * tab: the classroom page is what sends the trainer's heartbeat, and with no
 * heartbeat the server's timeout worker ends a Zoho class after a few minutes.
 * It is also where the trainer ends the class.
 */

/** Route for the trainer's embedded classroom. */
export const instructorClassroomPath = (batchId) => `/i/classroom/${batchId}`;

/**
 * Does this class open in the LMS's own embedded classroom?
 *
 * @param {{ zoomMode?: string, meetingProvider?: string }} startResponse
 *        the body of `POST /batches/:id/start-meeting`
 * @returns {boolean}
 */
export function opensEmbeddedClassroom(startResponse) {
  if (!startResponse) return false;
  if (startResponse.meetingProvider === "grats" || startResponse.meetingProvider === "zoho") return true;
  return startResponse.zoomMode === "v2" || startResponse.zoomMode === "platformHosted";
}

/**
 * Take the trainer wherever the class actually is.
 *
 * A full-page navigation, not `navigate()`: the Zoom Meeting SDK owns global
 * DOM state (`#zmmtg-root`, WASM, listeners) and initialises reliably only on a
 * clean page. Grats Meet does not need that, but sharing one entry point is
 * worth more than saving it one reload.
 *
 * @param {string} batchId
 * @param {Object} startResponse  body of the start-meeting call
 * @param {Object} [batch]        used only for the legacy plain-link fallback
 */
export function enterLiveClass(batchId, startResponse, batch) {
  if (opensEmbeddedClassroom(startResponse)) {
    window.location.href = instructorClassroomPath(batchId);
    return;
  }
  if (batch?.meetingLink) window.open(batch.meetingLink, "_blank");
}
